import { SearchClient } from 'algoliasearch'

import { ReactNode, ReactElement } from 'react'
import { Centre } from '../../../types/contentful-v1'

export type MarketplaceIndex = 'products_au' | 'categories_au'

export type ContentfulIndex =
  | 'retailer'
  | 'store'
  | 'site'
  | 'online_service'
  | 'service'
  | 'offer'
  | 'event'
  | 'news'
  | 'story'
  | 'centre'

export type Index = MarketplaceIndex | ContentfulIndex

// Expected shape of Algolia hits for various indexes:

export interface AlgoliaHit {
  objectID: string
  isEvolvedOnly?: boolean
}

export interface CategoryHit extends AlgoliaHit {
  displayTitle: string
  path: string[]
}

interface Variant {
  price?: {
    originalPrice: number
    effectivePrice: number
    discountedPrice?: number
  }
  sku: string
  onSale: boolean
}

export interface ProductHit extends AlgoliaHit {
  name: string
  shop: { id: number; name: string }
  description?: string
  store?: string
  image?: string
  brand: { name: string; slug: string }
  variant: Variant
  variantGroupCode: string
  otherVariants: Variant[]
}

export type MarketplaceHit = ProductHit | CategoryHit

export interface ProductHitWithBrandAndName extends ProductHit {
  brandAndName: string
}

export interface ContentfulHit extends AlgoliaHit {
  slug: string
  img: string
  title: string
}

export interface PageHit extends ContentfulHit {
  href: string
}

export interface StoreHit extends ContentfulHit {
  centreSlug: string
  centreName: string
}

export interface OfferHit extends ContentfulHit {
  storeTitles: string[]
}

export interface PostHit extends ContentfulHit {
  shortDescription: string
}

export type Hit<T extends Index> = T extends 'products_au'
  ? ProductHit
  : T extends 'categories_au'
  ? CategoryHit
  : T extends 'store'
  ? StoreHit
  : T extends 'site' | 'centre' | 'online_service'
  ? PageHit
  : T extends 'offer'
  ? OfferHit
  : T extends 'event' | 'story' | 'news'
  ? PostHit
  : ContentfulHit

export type SearchParams = Parameters<SearchClient['search']>[0][0]['params']

export interface Query<T extends Index> {
  indexName: T
  query: string
  params?: SearchParams
}

export interface Section<T extends Index> extends Query<T> {
  heading: string
  render: React.FC<Hit<T>>
}

export type ContentfulSections = Section<ContentfulIndex>[]
export type MarketplaceSections = Section<MarketplaceIndex>[]
export type AllSections = Section<Index>[]

export interface RawResults {
  hits: AlgoliaHit[]
}

export interface RenderedSectionResults {
  heading: string
  hits: ReactNode[]
}

export interface AlgoliaApiKeys {
  contentApplicationId?: string
  contentApiKey?: string
  marketplaceApplicationId?: string
  marketplaceApiKey?: string
}

export interface CoreSearchProps {
  country: string
  algoliaApiKeys: AlgoliaApiKeys
  centre?: Centre
}

export interface ModalSearchProps extends CoreSearchProps {
  children(showSearch: () => void): ReactElement
}

export interface EmbeddedSearchProps extends CoreSearchProps {
  showFixedQuery: string
}

export function isEmbeddedSearchProps(
  props: SearchProps
): props is EmbeddedSearchProps {
  return typeof (props as EmbeddedSearchProps).showFixedQuery === 'string'
}

export type SearchProps = ModalSearchProps | EmbeddedSearchProps

export interface SearchContext {
  country: string
  getOffsetlessTimeInDefaultTz: () => number
  centre?: Centre
  getOffsetlessTimeInCentre?: () => number
  isEvolved?: boolean
}

export enum Segment {
  'retailers' = 'retailers',
  'stores' = 'stores',
  'sites' = 'sites',
  'services' = 'services',
  'offers' = 'offers',
  'events' = 'events',
  'news' = 'news',
  'localStories' = 'localStories',
  'nationalStories' = 'nationalStories',
  'centres' = 'centres',
  'onlineServices' = 'onlineServices',
  'nearCentreStores' = 'nearCentreStores',
  'products' = 'products',
  'categories' = 'categories',
}

export type SegmentHits = Partial<Record<Segment, AlgoliaHit[]>>

// Render types

export type RenderContext = Omit<
  SearchContext,
  'getOffsetlessTimeInDefaultTz' | 'country'
>

export type OnResultClick = (hit: ContentfulHit | MarketplaceHit) => void

export interface SegmentRenderProps<I extends Index> {
  context: RenderContext
  onResultClick: OnResultClick
  hit: Hit<I>
}

export type SegmentRender<I extends Index> = React.FC<SegmentRenderProps<I>>

export interface GroupAndRenderSegmentsArgs {
  segmentHits: SegmentHits
  context: RenderContext
  onResultClickHeading: (
    heading: string,
    hit: ContentfulHit | MarketplaceHit
  ) => void
}

export interface GroupedHit {
  hit: AlgoliaHit
  segment: Segment
  onResultClick: OnResultClick
  context: RenderContext
}

export interface HitsGroup {
  hits: GroupedHit[]
  heading: string
}
